import {Divider, Stack, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {SpecialtyNotAvailableOnlineCard} from 'presentation/modules/booking/shared/ui/components/SpecialtyNotAvailableOnlineCard';
import {UnitCard} from 'presentation/modules/booking/shared/ui/components/UnitCard';
import {UnitNetworkHeader} from 'presentation/modules/booking/shared/ui/components/UnitNetworkHeader';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {EmptyAppointmentsCard} from '../EmptyAppointmentsCard';
import {EmptySelfNetworkAlert} from 'presentation/modules/booking/shared/ui/components/EmptySelfNetworkAlert';
import {useHealthUnitsSelfNetwork} from './hooks/useHealthUnitsSelfNetwork';
import {StyledButton} from './styles';
import {HealthUnitsSelfNetworkProps} from './types';

const {selfNetwork} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function HealthUnitsSelfNetwork({
	handleNextStep,
	unitsList,
	showEmptyAppointmentsCard,
	showSpecialtyNotAvailableCard,
}: HealthUnitsSelfNetworkProps) {
	const {
		handleSelectCard,
		query,
		isMobile,
		unitsByLocality,
		specialtyName,
		unitsByDistance,
		listByDistanceLength,
		unitsByDistanceTitle,
		shouldHideSelfNetworkComponent,
		shouldHideDivider,
		shouldHideSelfNetworkButton,
		getButtonAction,
		getButtonLabel,
		getButtonDataTagId,
		getEmptyAlertDescription,
		showEmptyAlert,
		showUnitsByDistanceTitle,
		shouldReverseNetworkOrder,
		isUnitsNetworksEnabled,
	} = useHealthUnitsSelfNetwork({
		list: unitsList,
		handleNextStep,
	});

	if (shouldHideSelfNetworkComponent()) return null;

	if (showEmptyAppointmentsCard && !isUnitsNetworksEnabled) {
		return <EmptyAppointmentsCard />;
	}

	if (showSpecialtyNotAvailableCard) {
		return <SpecialtyNotAvailableOnlineCard selfNetworkVariant />;
	}

	return (
		<Stack gap={2}>
			{shouldHideDivider || !shouldReverseNetworkOrder ? null : <Divider sx={{mt: 3}} />}

			{showEmptyAlert ? (
				<EmptySelfNetworkAlert description={getEmptyAlertDescription()} />
			) : (
				<Fragment>
					<UnitNetworkHeader
						title={selfNetwork.title}
						description={selfNetwork.onlineScheduleDescription}
					/>

					{unitsByLocality && unitsByLocality?.length > 0 ? (
						<Grid container spacing={2}>
							{unitsByLocality?.map((unit, index) => {
								return (
									<Grid
										key={`${unit.name}-${unit.formattedAddress}${index}`}
										size={{xs: isMobile ? 12 : 6}}>
										<UnitCard
											query={query}
											isNotSelf={false}
											onClick={() =>
												handleSelectCard({
													district: unit?.district,
													code: unit?.code,
													address: unit?.formattedAddress,
													name: unit?.name,
													isTelemedicine: false,
													phoneNumbers: unit?.phoneNumbers,
													cityName: unit?.city,
													ufName: unit?.stateName,
													ufCode: unit?.stateCode,
												})
											}
											unitName={unit?.name}
											address={unit?.formattedAddress}
											nmBairroOrigem={unit?.nmBairroOrigem}
											isTelemedicine={false}
											distanceReference={unit?.distance}
											specialtyName={specialtyName}
											phoneNumbers={unit?.phoneNumbers}
										/>
									</Grid>
								);
							})}
						</Grid>
					) : null}

					{unitsByDistance && listByDistanceLength > 0 ? (
						<Fragment>
							{showUnitsByDistanceTitle() ? (
								<Typography
									variant="subtitle2"
									color="text.secondary"
									lineHeight={1.75}>
									{unitsByDistanceTitle}
								</Typography>
							) : null}

							<Grid container spacing={2}>
								{unitsByDistance?.map((unit, index) => {
									return (
										<Grid
											key={`${unit.name}-${unit.formattedAddress}${index}`}
											size={{xs: isMobile ? 12 : 6}}>
											<UnitCard
												query={query}
												isNotSelf={false}
												showDistanceReference
												onClick={() =>
													handleSelectCard({
														district: unit?.district,
														code: unit?.code,
														address: unit?.formattedAddress,
														name: unit?.name,
														isTelemedicine: false,
														phoneNumbers: unit?.phoneNumbers,
														cityName: unit?.city,
														ufName: unit?.stateName,
														ufCode: unit?.stateCode,
													})
												}
												unitName={unit?.name}
												address={unit?.formattedAddress}
												nmBairroOrigem={unit?.nmBairroOrigem}
												isTelemedicine={false}
												distanceReference={unit?.distance}
												specialtyName={specialtyName}
											/>
										</Grid>
									);
								})}
							</Grid>
						</Fragment>
					) : null}
				</Fragment>
			)}

			<Stack gap={2}>
				{shouldHideSelfNetworkButton ? null : (
					<StyledButton
						data-tag-id={getButtonDataTagId()}
						variant="outlined"
						size="large"
						onClick={getButtonAction}>
						{getButtonLabel()}
					</StyledButton>
				)}

				{shouldHideDivider || shouldReverseNetworkOrder ? null : <Divider sx={{mb: 2}} />}
			</Stack>
		</Stack>
	);
}
